import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { useMutation } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import type { TAccountProduct } from '../../common';
import { createMutationPromise } from '../../common/utils/queries';
import { mutateProductFees, useAccountStore } from '../../contexts';
import type { TTableCellContent } from '../atoms';
import { Block, PrimaryIconButton } from '../atoms';
import {
  ProductMatrixWrapper,
  SimpleTable,
  SimpleTableRow
} from '../molecules';
import type { FEE_IDS } from '../types';
import type {
  AccountProductBlockRow,
  AccountProductBlock
} from '../types/Product';

const SimpleTableTitle = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.margins.md};

  > h1,
  > h2,
  > h3,
  > h4,
  > h5,
  > h6 {
    margin: 0;
  }
`;

const SimpleTableColumns = styled.span<{ cols?: number }>(
  ({ theme, cols = 2 }) => css`
    display: grid;
    grid-gap: ${theme.margins.lg};
    grid-auto-flow: column;
    grid-template-columns: repeat(${cols}, minmax(0, 1fr));
  `
);

const CustomFeeBlock = ({
  product,
  merchantId,
  size,
  title = '',
  id,
  columns,
  schema,
  editBlock,
  matrix
}) => {
  const [productUpdateNonce, setProductUpdateNonce] = useState(uuidv4());

  const updateProductFees = useAccountStore((s) => s.updateProductFees);
  const updateProductFeesMutation = mutateProductFees({
    productId: product.id,
    merchantId,
    updateProductFees,
    mutate: useMutation
  });

  const removeFeeConditions = async (id: FEE_IDS): Promise<TAccountProduct> => {
    const { fees = {} } = product;
    const updatedFees: TAccountProduct['fees'] = {
      ...fees,
      [id]: {
        ...fees[id],
        conditions: { terms: [] }
      }
    };

    const mutation = await createMutationPromise(
      updateProductFeesMutation,
      updatedFees
    );

    return mutation;
  };

  useEffect(() => {
    setProductUpdateNonce(uuidv4());
  }, [product]);

  const visibleCols = columns
    .map((c: AccountProductBlock['modal']['columns'][number]) =>
      c.filter?.(({ hidden = false }) => !hidden)
    )
    .filter((c) => c.length);

  return (
    <Block size={size} key={title} divided>
      {editBlock && visibleCols.some((c) => c.some((c) => !c.hidden)) && (
        <div>
          <SimpleTableTitle>
            <h2>{title}</h2>
            <PrimaryIconButton type="button" onClick={editBlock.onEdit}>
              <span>{editBlock.label}</span>
              <span className="material-symbols-rounded">edit</span>
            </PrimaryIconButton>
          </SimpleTableTitle>

          <SimpleTableColumns
            cols={visibleCols.length === 1 && size === 'half' ? 1 : 2}
          >
            {visibleCols.map((column, i) => {
              return (
                <div key={`${title}-column-${i}`}>
                  <SimpleTable>
                    {column.map(
                      ({
                        title: rowTitle,
                        content,
                        nonEditable,
                        ...row
                      }: AccountProductBlockRow) => {
                        if (row.contentType === 'table') {
                          return null;
                        }

                        const cellData = {
                          content:
                            typeof content === 'function' ? content() : content,
                          contentType: row.contentType
                        } as TTableCellContent;

                        return (
                          <SimpleTableRow
                            compact
                            key={rowTitle}
                            headerCellWidth={
                              columns.length === 1 && size === 'half'
                                ? '40% !important'
                                : undefined
                            }
                            title={rowTitle}
                            {...row}
                            {...cellData}
                          />
                        );
                      }
                    )}
                  </SimpleTable>
                </div>
              );
            })}
          </SimpleTableColumns>
        </div>
      )}

      {matrix && (
        <div key={`${productUpdateNonce}-${id}`}>
          <ProductMatrixWrapper
            product={product}
            removeFeeConditions={removeFeeConditions}
            feeId={id as FEE_IDS}
            title={title.replace(' Settings', '')}
          >
            <ProductMatrixWrapper.Conditions>
              {matrix.FooterBlock()}
            </ProductMatrixWrapper.Conditions>
            <ProductMatrixWrapper.Initial>
              {matrix.FooterInitial()}
            </ProductMatrixWrapper.Initial>
          </ProductMatrixWrapper>
        </div>
      )}
    </Block>
  );
};

export { CustomFeeBlock };
